/**
 * Settings → API activity — the egress observability panel
 * (CD-REQUEST-api-observability v2, 2026-08-02).
 *
 * Reads the in-client telemetry buffer (`@v0y4ge/api-client` telemetry.ts):
 * every request the app issues is recorded at the four fetch choke points in
 * client.ts — count, result bucket, and duration with p50/p95. NO backend op,
 * NO store, NO bodies/headers/user attribution. The identical code path runs
 * in preview and deployed, so this panel renders the same in both worlds by
 * construction; in CD the "backend" underneath is the seed/serving layer.
 */
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { getApiCallStats, getRecentApiCalls, resetApiCallTelemetry, type ApiCallStats } from "@v0y4ge/api-client";
import { Btn, Card, Icon, PageLayout, T, toneColors } from "@v0y4ge/jrni-ui";
import { PageHeader } from "../../shell/chrome";
import SettingsShell from "./SettingsShell";
import { StatTile } from "../../shell/composites";
import { useT } from "../../lib/i18n";
import { friendlyTime } from "../../lib/datetime";

const OP_COLS = "minmax(220px, 2fr) 70px 70px 150px 70px 70px 90px";
const RECENT_COLS = "90px minmax(180px, 1.6fr) minmax(200px, 2fr) 90px 80px";

type ApiResult = "ok" | "client_error" | "server_error";

/** Result → canonical `Tone`. The two chips below were byte-identical in style and
 * each carried its own copy of this mapping; one map, one chip. */
const RESULT_TONE: Record<ApiResult, "success" | "warning" | "danger"> = {
  ok: "success", client_error: "warning", server_error: "danger",
};

/** The numeric/status pill — tinted, so it takes `toneColors()`'s bg/fg pair,
 * whose halves invert together. (The solid counterpart is `toneFill()`.) */
function ResultPill({ result, children }: { result: ApiResult; children: ReactNode }) {
  const { bg, fg } = toneColors(RESULT_TONE[result]);
  return (
    <span style={{
      fontSize: T.text.label.fontSize, fontWeight: 700, color: fg, background: bg,
      borderRadius: T.radius.pill,
      padding: `1px ${T.space[2]}`, /* optical — 1px keeps the pill on the row's 20px cap-height */
      fontVariantNumeric: "tabular-nums",
    }}>
      {children}
    </span>
  );
}

function ResultChip({ kind, count }: { kind: ApiResult; count: number }) {
  if (!count) return <span style={{ fontSize: T.text.label.fontSize, color: T.soft }}>0</span>;
  return <ResultPill result={kind}>{count}</ResultPill>;
}

function StatusChip({ status, result }: { status: number; result: ApiResult }) {
  return <ResultPill result={result}>{status === 0 ? "network" : status}</ResultPill>;
}

export default function ApiObservability() {
  const t = useT();
  const [paused, setPaused] = useState(false);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (paused) return;
    const h = setInterval(() => setTick((n) => n + 1), 2000);
    return () => clearInterval(h);
  }, [paused]);

  const stats: ApiCallStats = useMemo(() => getApiCallStats(), [tick, paused]);
  const recent = useMemo(() => getRecentApiCalls(50), [tick, paused]);
  const errCount = stats.byResult.client_error + stats.byResult.server_error;

  // `background` was the literal #FAFAFD — the exact value `T.cardSoft`
  // (--jrni-color-surface-card-soft) exists to replace. Hardcoded, it stayed
  // near-white under [data-theme="dark"], so both table header rows rendered as
  // a white band on a dark card with T.soft ink on top. The token adapts.
  const headCell = { display: "grid" as const, padding: `${T.space[2]} ${T.space[4]}`, borderBottom: `1px solid ${T.border}`, background: T.cardSoft, gap: T.space[2], alignItems: "center" as const, fontSize: T.text.label.fontSize, fontWeight: 700, color: T.soft, textTransform: "uppercase" as const, letterSpacing: T.letterSpacing.wide };
  const rowCell = { display: "grid" as const, padding: `${T.space[2]} ${T.space[4]}`, borderBottom: `1px solid ${T.n6}`, gap: T.space[2], alignItems: "center" as const, fontSize: T.text.bodySm.fontSize, color: T.n1 };
  const mono = { fontFamily: T.fontMono, fontSize: T.text.label.fontSize, color: T.soft };

  return (
    <SettingsShell activeTab="api">
      <PageHeader
        title={t("settings.apiActivity.title", "API activity")}
        subtitle={t("settings.apiActivity.subtitle", "Every request this session has issued — call counts by operation, result breakdown, and duration percentiles. Captured inside the API client at egress, so preview and deployed report identically.")}
        actions={<>
          <Btn variant="secondary" icon={<Icon.eye size={14} />} onClick={() => setPaused((p) => !p)}>
            {paused ? t("settings.apiActivity.resume", "Resume") : t("settings.apiActivity.pause", "Pause")}
          </Btn>
          <Btn variant="secondary" icon={<Icon.refresh size={14} />} onClick={() => { resetApiCallTelemetry(); setTick((n) => n + 1); }}>
            {t("settings.apiActivity.reset", "Reset")}
          </Btn>
        </>}
      />
      {/* Was a hand-rolled scroll body: flex/minHeight/overflow + padding "16px 24px 24px"
          + gap 14. PageLayout declares that exact gutter from the space scale, so this is a
          drop-in — the only change is gap 14 -> 16, onto the scale. */}
      <PageLayout>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: T.space[3] }}>
          <StatTile label={t("settings.apiActivity.totalCalls", "API calls (this session)")} value={String(stats.totalCalls)} delta={`${stats.byOperation.length} ${t("settings.apiActivity.distinctOperations", "distinct operations")}`} icon={<Icon.network size={14} />} />
          <StatTile label={t("settings.apiActivity.errors", "Errors")} value={String(errCount)} delta={errCount ? `${stats.byResult.client_error} × 4xx · ${stats.byResult.server_error} × 5xx/network` : t("settings.apiActivity.allClear", "All clear")} accent={errCount ? T.orange1 : T.green1} icon={<Icon.warn size={14} />} />
          <StatTile label={t("settings.apiActivity.latency", "Latency (p50 / p95)")} value={stats.totalCalls ? `${stats.overall.p50Ms} / ${stats.overall.p95Ms} ms` : "—"} delta={t("settings.apiActivity.startToFinishAtEgress", "Start-to-finish, measured at egress")} accent={T.green1} icon={<Icon.clock size={14} />} />
        </div>

        <Card style={{ padding: 0, overflow: "hidden", flexShrink: 0 }}>
          <div style={{ ...headCell, gridTemplateColumns: OP_COLS }}>
            <span>{t("settings.apiActivity.operation", "Operation")}</span>
            <span>{t("settings.apiActivity.method", "Method")}</span>
            <span>{t("settings.apiActivity.calls", "Calls")}</span>
            <span>{t("settings.apiActivity.okClientServer", "ok · 4xx · 5xx")}</span>
            <span>p50</span>
            <span>p95</span>
            <span>{t("settings.apiActivity.last", "Last")}</span>
          </div>
          {stats.byOperation.length === 0 ? (
            <div style={{ padding: T.space[8], textAlign: "center", color: T.soft, fontSize: T.text.bodySm.fontSize }}>
              {t("settings.apiActivity.noCallsYet", "No API calls recorded yet this session — navigate the app and they'll appear here live.")}
            </div>
          ) : stats.byOperation.map((op) => (
            <div key={op.operationId + op.method + op.pathTemplate} style={{ ...rowCell, gridTemplateColumns: OP_COLS }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{op.operationId}</div>
                <div style={{ ...mono, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{op.pathTemplate}</div>
              </div>
              <span style={{ fontWeight: 700, fontSize: T.text.label.fontSize, color: T.soft }}>{op.method}</span>
              <span style={{ fontVariantNumeric: "tabular-nums", fontWeight: 600 }}>{op.count}</span>
              <span style={{ display: "flex", gap: T.space[2], alignItems: "center" }}>
                <ResultChip kind="ok" count={op.ok} />
                <ResultChip kind="client_error" count={op.client_error} />
                <ResultChip kind="server_error" count={op.server_error} />
              </span>
              <span style={{ fontVariantNumeric: "tabular-nums" }}>{t("common.units.millisecondsMs", "{n} ms").replace("{n}", String(op.p50Ms))}</span>
              <span style={{ fontVariantNumeric: "tabular-nums" }}>{t("common.units.millisecondsMs", "{n} ms").replace("{n}", String(op.p95Ms))}</span>
              <span style={{ fontSize: T.text.label.fontSize, color: T.soft }}>{friendlyTime(op.lastAt)}</span>
            </div>
          ))}
        </Card>

        <Card style={{ padding: 0, overflow: "hidden", flexShrink: 0 }}>
          <div style={{ ...headCell, gridTemplateColumns: RECENT_COLS }}>
            <span>{t("settings.apiActivity.when", "When")}</span>
            <span>{t("settings.apiActivity.operation", "Operation")}</span>
            <span>{t("settings.apiActivity.request", "Request")}</span>
            <span>{t("settings.apiActivity.status", "Status")}</span>
            <span>{t("settings.apiActivity.duration", "Duration")}</span>
          </div>
          {recent.length === 0 ? (
            <div style={{ padding: T.space[8], textAlign: "center", color: T.soft, fontSize: T.text.bodySm.fontSize }}>
              {t("settings.apiActivity.recentEmpty", "Recent calls appear here, newest first.")}
            </div>
          ) : recent.map((r, i) => (
            <div key={r.occurredAt + r.pathTemplate + i} style={{ ...rowCell, gridTemplateColumns: RECENT_COLS }}>
              <span style={{ fontSize: T.text.label.fontSize, color: T.soft, fontVariantNumeric: "tabular-nums" }}>{friendlyTime(r.occurredAt)}</span>
              <span style={{ fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.operationId}</span>
              <span style={{ ...mono, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.method} {r.pathTemplate}</span>
              <StatusChip status={r.status} result={r.result} />
              <span style={{ fontVariantNumeric: "tabular-nums" }}>{t("common.units.millisecondsMs", "{n} ms").replace("{n}", String(r.durationMs))}</span>
            </div>
          ))}
        </Card>

        {/* No padding of its own — PageLayout's 16px gap and 24px bottom gutter
            already separate this note from the card above and the viewport edge. */}
        <div style={{ fontSize: T.text.label.fontSize, color: T.soft, textAlign: "center" }}>
          {t("settings.apiActivity.provenance", "Session-scoped, in-memory, capped at 500 records — no bodies, headers, or user attribution are captured. In Claude Design the backend underneath is the seed/serving layer; deployed, it's the real tenant API. Same recorder either way.")}
        </div>
      </PageLayout>
    </SettingsShell>
  );
}
